import React, { useEffect, useState } from 'react';
import '../../component-styles/TimeBillingSettings.css';
import API_BASE from '../../utils/api.js';

function EditableStringList({ title, addLabel, emptyLabel, fieldName, itemLabel }) {
  const apiUrl = API_BASE;
  const userId = localStorage.getItem('userId');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setError('Missing user session. Please sign in again.');
      return;
    }

    async function load() {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${apiUrl}/api/settings/${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error(`Load failed (${res.status})`);
        const data = await res.json();
        const list = Array.isArray(data[fieldName]) ? data[fieldName] : [];
        setItems(list.map((item) => String(item || '').trim()).filter(Boolean));
      } catch (err) {
        console.error(`Failed to load ${fieldName}:`, err);
        setError(`Could not load ${itemLabel.toLowerCase()}s.`);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [apiUrl, userId, fieldName, itemLabel]);

  async function saveItems(next) {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${apiUrl}/api/settings/${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [fieldName]: next }),
      });
      if (!res.ok) throw new Error('save_failed');
      setItems(next);
      return true;
    } catch (err) {
      console.error(`Failed to save ${fieldName}:`, err);
      setError('Failed to save changes. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  function openAdd() {
    setEditingIndex(null);
    setName('');
    setShowModal(true);
  }

  function openEdit(index) {
    setEditingIndex(index);
    setName(items[index] || '');
    setShowModal(true);
  }

  async function handleDelete(index) {
    const next = items.filter((_, i) => i !== index);
    await saveItems(next);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;

    const duplicateIndex = items.findIndex(
      (item, idx) => idx !== editingIndex && item.toLowerCase() === trimmed.toLowerCase()
    );
    if (duplicateIndex >= 0) return;

    const next = [...items];
    if (editingIndex === null) {
      next.push(trimmed);
    } else {
      next[editingIndex] = trimmed;
    }

    const ok = await saveItems(next);
    if (ok) setShowModal(false);
  }

  return (
    <div className="tbset-container">
      <div className="tbset-header">
        <h4 className="tbset-section-title">{title}</h4>
        <button className="tbset-add-btn" onClick={openAdd} disabled={loading}>
          {addLabel}
        </button>
      </div>
      {error ? <div className="tbset-error">{error}</div> : null}

      <div className="tbset-table">
        <table>
          <thead>
            <tr>
              <th>{itemLabel}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading &&
              items.map((item, idx) => (
                <tr key={`${item}-${idx}`}>
                  <td>{item}</td>
                  <td className="tbset-actions">
                    <button type="button" onClick={() => openEdit(idx)}>
                      Edit
                    </button>
                    <button type="button" className="danger" onClick={() => handleDelete(idx)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

            {!loading && items.length === 0 && (
              <tr>
                <td colSpan="2" className="tbset-empty">
                  {emptyLabel}
                </td>
              </tr>
            )}

            {loading && (
              <tr>
                <td colSpan="2" className="tbset-empty">
                  {saving ? 'Saving...' : `Loading ${itemLabel.toLowerCase()}s...`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="tbset-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="tbset-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingIndex === null ? `Add ${itemLabel}` : `Edit ${itemLabel}`}</h3>
            <form onSubmit={handleSubmit}>
              <label>{itemLabel} Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required />
              <div className="tbset-modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const INVOICE_LAYOUT_OPTIONS = [
  {
    value: 'grouped',
    label: 'Grouped by activity',
    description: 'A separate table for each activity, with the activity as its heading.',
  },
  {
    value: 'list',
    label: 'Single list',
    description: 'Every entry in one table, with Activity as a column.',
  },
];

function InvoiceLayoutSetting() {
  const apiUrl = API_BASE;
  const userId = localStorage.getItem('userId');

  const [layout, setLayout] = useState('grouped');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    async function load() {
      try {
        const res = await fetch(`${apiUrl}/api/settings/${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error(`Load failed (${res.status})`);
        const data = await res.json();
        setLayout(data.timeBillingInvoiceLayout === 'list' ? 'list' : 'grouped');
      } catch (err) {
        console.error('Failed to load timeBillingInvoiceLayout:', err);
        setError('Could not load the invoice layout.');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [apiUrl, userId]);

  async function handleChange(next) {
    const previous = layout;
    setLayout(next);
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${apiUrl}/api/settings/${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timeBillingInvoiceLayout: next }),
      });
      if (!res.ok) throw new Error('save_failed');
    } catch (err) {
      console.error('Failed to save timeBillingInvoiceLayout:', err);
      setLayout(previous);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="tbset-container">
      <div className="tbset-header">
        <h4 className="tbset-section-title">Invoice Layout</h4>
      </div>
      <p className="tbset-help">
        How line items are laid out on new time billing invoices. You can still switch any
        invoice from its own page.
      </p>
      {error ? <div className="tbset-error">{error}</div> : null}

      <div className="tbset-layout-options" role="radiogroup" aria-label="Invoice layout">
        {INVOICE_LAYOUT_OPTIONS.map((option) => (
          <label
            key={option.value}
            className={`tbset-layout-option${layout === option.value ? ' selected' : ''}`}
          >
            <input
              type="radio"
              name="timeBillingInvoiceLayout"
              value={option.value}
              checked={layout === option.value}
              disabled={loading || saving || !userId}
              onChange={() => handleChange(option.value)}
            />
            <span>
              <span className="tbset-layout-label">{option.label}</span>
              <span className="tbset-layout-description">{option.description}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function TimeBillingSettings() {
  return (
    <div className="tbset-sections">
      <InvoiceLayoutSetting />
      <EditableStringList
        title="Activities"
        addLabel="+ Add Activity"
        emptyLabel="No custom activities yet."
        fieldName="timeActivities"
        itemLabel="Activity"
      />
      <EditableStringList
        title="Non-Project Categories"
        addLabel="+ Add Category"
        emptyLabel="No custom categories yet."
        fieldName="timeCategories"
        itemLabel="Category"
      />
    </div>
  );
}
